import React, { useEffect, useMemo } from 'react';
import { useTheme } from '@mui/styles';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
  VictoryStack,
  VictoryTooltip,
  VictoryGroup
} from 'victory';
import { Grid, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';
import { getFormattedDuration } from 'modules/notes/common/components/Timer/useFormattedDuration';
import { NoteInsightsViewOptions } from 'modules/Insights/Notes/NoteInsightsContextProvider';
import { useMessages } from 'modules/intl';
import { domainPaddingType, useChartStyles } from './hooks/useChartStyles';
import { dataType } from './utils';
import Footer from './Footer';
import { useMyUtilizationDataPagination } from './hooks/useMyUtilizationDataPagination';

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    paddingTop: theme.spacing(4)
  }
}));

const MyCustomFlyout: React.FC<any> = ({
  x,
  y,
  name,
  datum
}: {
  x: number;
  y: number;
  name: string;
  datum: any;
}) => {
  const { formatMessage } = useIntl();

  const formattedTime = getFormattedDuration({
    formatMessage,
    duration: datum.totalDuration ?? 0,
    needsMask: true
  });
  return (
    <foreignObject x={x} y={y - 40} width="90" height="50">
      <Tooltip title={`${name}: ${formattedTime}`} open={true}>
        <span />
      </Tooltip>
    </foreignObject>
  );
};

type ProductivityChartProp = {
  selectedViewOptionSupervisor: NoteInsightsViewOptions;
  width: number | undefined;
  tData: any[];
  setCurrentChartData: React.Dispatch<React.SetStateAction<dataType[]>>;
};
const labelNames = [
  'insights.productivityChart.work-schedule',
  'insights.productivityChart.unaccounted-hours',
  'insights.productivityChart.time-entry-billable-duration',
  'insights.productivityChart.time-entry-non-billable-duration'
];
const sortByArray = [
  'work-schedule',
  'unaccounted-hours',
  'time-entry-billable-duration',
  'time-entry-non-billable-duration'
];

const ProductivityChart: React.FC<ProductivityChartProp> = ({
  width,
  tData,
  selectedViewOptionSupervisor,
  setCurrentChartData
}: ProductivityChartProp) => {
  const labels = useMessages(labelNames);
  const theme = useTheme();
  const {
    onNext,
    onPrevious,
    showNext,
    showPrev,
    currentData,
    dateSet
  } = useMyUtilizationDataPagination(tData, selectedViewOptionSupervisor, 10);
  const dataWithChangedName = useMemo(() => {
    currentData.sort(
      (a, b) => sortByArray.indexOf(a.name) - sortByArray.indexOf(b.name)
    );
    return currentData.map((i) => {
      i.name = labels[`insights.productivityChart.${i.name}`];
      return i;
    });
  }, [currentData, labels]);
  useEffect(() => {
    setCurrentChartData(dataWithChangedName);
  }, [dataWithChangedName, setCurrentChartData]);

  const {
    scheduleData,
    unAccountedData,
    billingNonBillingData
  } = useMemo(() => {
    return dataWithChangedName.reduce(
      (acc, item) => {
        if (item.name === labels['insights.productivityChart.work-schedule'])
          acc.scheduleData = item;
        else if (
          item.name === labels['insights.productivityChart.unaccounted-hours']
        )
          acc.unAccountedData = item;
        else acc.billingNonBillingData.push(item);
        return acc;
      },
      {
        scheduleData: undefined,
        unAccountedData: undefined,
        billingNonBillingData: []
      } as {
        scheduleData: dataType | undefined;
        unAccountedData: dataType | undefined;
        billingNonBillingData: dataType[];
      }
    );
  }, [dataWithChangedName, labels]);

  const style = useChartStyles(theme);
  const classes = useStyles();
  const barStyling = useMemo(() => {
    const noOfBars = currentData[0]?.data.length || 0;
    const widthContainer = (width || 0) - 100;
    let bWidth = 0;
    let dPadding = {};
    if (noOfBars <= 4) {
      {
        bWidth = ((widthContainer || 0) - 300) / ((noOfBars + 1) * 3);
        dPadding = { x: [40, 40], y: [0, 10] };
      }
    } else if (noOfBars <= 7) {
      bWidth = ((widthContainer || 0) - 300) / 22;
      dPadding = { x: [80, 80], y: [0, 10] };
    } else if (noOfBars <= 10) {
      bWidth = ((widthContainer || 0) - 300) / 34;
      dPadding = { x: [55, 55], y: [0, 10] };
    }
    return {
      barWidth: bWidth,
      domainPadding: dPadding as domainPaddingType
    };
  }, [currentData, width]);
  const tickValues = useMemo(() => dateSet.map((_z, index) => index + 1), [
    dateSet
  ]);

  const tickFormat = useMemo(
    () => dateSet.map((z) => DateTime.fromISO(z, { zone: 'utc' }).day),
    [dateSet]
  );

  return (
    <>
      <Grid
        item
        container
        className={classes.chartContainer}
        data-testid={'productivity-chart'}
      >
        <VictoryChart
          domainPadding={barStyling.domainPadding}
          height={style.VictoryChart.height}
          width={width}
          padding={style.VictoryChart.padding}
          containerComponent={<VictoryContainer responsive={true} />}
          data-testid={'productivity-victory-chart'}
        >
          <VictoryAxis
            tickValues={tickValues}
            tickFormat={tickFormat}
            style={style.VictoryAxis}
          />
          <VictoryAxis
            dependentAxis
            tickFormat={(x) => `${(x / 3600).toFixed(2)}h`}
            style={style.VictoryAxis}
          />
          <VictoryGroup offset={barStyling.barWidth + 5}>
            <VictoryStack>
              <VictoryBar
                data-testid={'productivity-schedule-bar'}
                barWidth={barStyling.barWidth}
                data={scheduleData.data}
                style={{
                  data: { fill: scheduleData.color }
                }}
                x="date"
                y="totalDuration"
                labels={() => ''}
                labelComponent={
                  <VictoryTooltip
                    flyoutPadding={10}
                    flyoutComponent={
                      <MyCustomFlyout name={scheduleData.name} />
                    }
                  />
                }
                events={[
                  {
                    target: 'data',
                    eventHandlers: {
                      onMouseOver: () => {
                        return [
                          {
                            target: 'data',
                            mutation: () => {
                              return {
                                style: {
                                  fill: scheduleData.color,
                                  stroke: theme.palette.information.background,
                                  strokeWidth: 1,
                                  cursor: 'pointer'
                                }
                              };
                            }
                          },
                          {
                            target: 'labels',
                            mutation: () => ({ active: true })
                          }
                        ];
                      },
                      onMouseLeave: () => {
                        return [
                          {
                            target: 'data',
                            mutation: () => {
                              return {
                                style: {
                                  fill: scheduleData.color,
                                  strokeWidth: 0
                                }
                              };
                            }
                          },
                          {
                            target: 'labels',
                            mutation: () => ({ active: undefined })
                          }
                        ];
                      }
                    }
                  }
                ]}
              />
            </VictoryStack>
            {unAccountedData && (
              <VictoryStack>
                <VictoryBar
                  data-testid={'productivity-unAccountedData-bar'}
                  barWidth={barStyling.barWidth}
                  data={unAccountedData.data || []}
                  style={{
                    data: { fill: unAccountedData.color }
                  }}
                  x="date"
                  y="totalDuration"
                  labels={() => ''}
                  labelComponent={
                    <VictoryTooltip
                      flyoutPadding={10}
                      flyoutComponent={
                        <MyCustomFlyout name={unAccountedData.name} />
                      }
                    />
                  }
                  events={[
                    {
                      target: 'data',
                      eventHandlers: {
                        onMouseOver: () => {
                          return [
                            {
                              target: 'data',
                              mutation: () => {
                                return {
                                  style: {
                                    fill: unAccountedData.color,
                                    stroke:
                                      theme.palette.information.background,
                                    strokeWidth: 1,
                                    cursor: 'pointer'
                                  }
                                };
                              }
                            },
                            {
                              target: 'labels',
                              mutation: () => ({ active: true })
                            }
                          ];
                        },
                        onMouseLeave: () => {
                          return [
                            {
                              target: 'data',
                              mutation: () => {
                                return {
                                  style: {
                                    fill: unAccountedData.color,
                                    strokeWidth: 0
                                  }
                                };
                              }
                            },
                            {
                              target: 'labels',
                              mutation: () => ({ active: undefined })
                            }
                          ];
                        }
                      }
                    }
                  ]}
                />
              </VictoryStack>
            )}
            {billingNonBillingData && billingNonBillingData.length > 0 && (
              <VictoryStack>
                {billingNonBillingData.map((x: dataType, _index: number) => {
                  return (
                    <VictoryBar
                      data-testid={`productivity-${x.name}-bar`}
                      key={x.name}
                      barWidth={barStyling.barWidth}
                      data={x.data}
                      style={{
                        data: { fill: x.color }
                      }}
                      x="date"
                      y="totalDuration"
                      labels={() => ''}
                      labelComponent={
                        <VictoryTooltip
                          flyoutPadding={10}
                          flyoutComponent={<MyCustomFlyout name={x.name} />}
                        />
                      }
                      events={[
                        {
                          target: 'data',
                          eventHandlers: {
                            onMouseOver: () => {
                              return [
                                {
                                  target: 'data',
                                  mutation: () => {
                                    return {
                                      style: {
                                        fill:
                                          billingNonBillingData[_index].color,
                                        stroke:
                                          theme.palette.information.background,
                                        strokeWidth: 1,
                                        cursor: 'pointer'
                                      }
                                    };
                                  }
                                },
                                {
                                  target: 'labels',
                                  mutation: () => ({ active: true })
                                }
                              ];
                            },
                            onMouseLeave: () => {
                              return [
                                {
                                  target: 'data',
                                  mutation: () => {
                                    return {
                                      style: {
                                        fill:
                                          billingNonBillingData[_index].color,
                                        strokeWidth: 0
                                      }
                                    };
                                  }
                                },
                                {
                                  target: 'labels',
                                  mutation: () => ({ active: undefined })
                                }
                              ];
                            }
                          }
                        }
                      ]}
                    />
                  );
                })}
              </VictoryStack>
            )}
          </VictoryGroup>
        </VictoryChart>
      </Grid>
      <Footer
        onNext={onNext}
        onPrevious={onPrevious}
        showNext={showNext}
        showPrev={showPrev}
      />
    </>
  );
};

export default React.memo(ProductivityChart);
